import { ResponseDeclaration } from "../../models"
import { CurrentTestState } from "../../storage"
import {
  Cardinality,
  InteractionType,
  onSaveStudentAnswer,
  SelectableChoiceConstraints
} from "../../types"
import { useEffect, useMemo, useRef, useState } from "react"
import { useSnapshot } from "valtio"
import {
  useResponseUpdate,
  useResponseValidityUpdate,
  useStateReset
} from "../hooks/itemResponses"
import { useResponseDeclaration } from "./common"

type SelectableChoiceType = {
  selected: string[]
  onSelect: (id: string) => void
  responseDeclaration: ResponseDeclaration
  isValid: boolean
  disabled: boolean
  isHotspotProcessing: boolean // For hotspot interactions ONLY
}

/**
 * Selection and response saving hook.
 * @param { String } responseIdentifier response declaration id.
 * @param { InteractionType } interactionType type of interaction.
 * @param { SelectableChoiceConstraints } constraints min and max choices.
 * @param { Boolean } allowSingleReselect automatically reselect item for single cardinality ("true" by default).
 * @returns { SelectableChoiceType }
 */
export const useSelectableChoice = (args: {
  responseIdentifier: string
  interactionType: InteractionType
  constraints?: SelectableChoiceConstraints
  allowSingleReselect?: boolean
  onSaveStudentAnswer?: onSaveStudentAnswer
}): SelectableChoiceType => {
  const {
    responseIdentifier,
    interactionType,
    constraints = {},
    allowSingleReselect = true,
    onSaveStudentAnswer
  } = args

  const { minChoices, maxChoices } = constraints

  const { currentItemResponse } = useSnapshot(CurrentTestState)
  const [selected, setSelected] = useState<string[]>([])
  const [id, setId] = useState("")
  const responseDeclaration = useResponseDeclaration(id, responseIdentifier)
  // Used for hotspot interactions ONLY
  const [isHotspotProcessing, setIsHotspotProcessing] = useState<boolean>(false)

  const lastSelectedId = useRef<string>(null)

  useEffect(() => {
    if (!currentItemResponse?.id) {
      return
    }

    setId(CurrentTestState.currentItemResponse.id)
  }, [currentItemResponse?.id, currentItemResponse])

  // Update response on selected items change.
  useResponseUpdate(id, responseIdentifier, selected, interactionType)
  const isValid = useResponseValidityUpdate(
    id,
    selected.length,
    minChoices,
    maxChoices
  )
  const cardinality =
    maxChoices === 1
      ? Cardinality.single
      : responseDeclaration?.cardinality || Cardinality.single
  const disabled = useMemo(
    () =>
      maxChoices &&
      selected.length === maxChoices &&
      cardinality !== Cardinality.single,
    [selected.length, maxChoices, cardinality]
  )

  // Reset selected items when question changes
  useStateReset(id, responseIdentifier, setSelected)

  const onSelect = (id: string) => {
    // Only set processing for hotspot interactions
    if (interactionType === InteractionType.hotspot) {
      setIsHotspotProcessing(true)
    }
    lastSelectedId.current = id

    setSelected(current => {
      if (current.includes(id)) {
        return current.filter(item => item !== id)
      }

      if (disabled && cardinality !== Cardinality.single) {
        return current
      }

      if (cardinality === Cardinality.single) {
        if (maxChoices > 1) {
          // Code for maxRetries & minCorrect - allows reselection
          return !current.length ? [id] : [...current, id]
        }
        return allowSingleReselect || !current.length ? [id] : current
      }

      // REVIEW: no need to handle "ordered" crdinality anymore?
      if (
        cardinality === Cardinality.multiple ||
        cardinality === Cardinality.ordered
      ) {
        return [...current, id]
      }

      return current
    })
  }

  useEffect(() => {
    if (!lastSelectedId.current) return

    // Call onSaveStudentAnswer for all interaction types
    onSaveStudentAnswer?.({
      interactionType,
      answerId: lastSelectedId.current,
      minChoices: minChoices,
      maxChoices: maxChoices
    })

    // For hotspot interactions, reset processing state after a delay
    if (interactionType === InteractionType.hotspot && isHotspotProcessing) {
      const timeoutId = setTimeout(() => {
        setIsHotspotProcessing(false)
        lastSelectedId.current = null
      }, 1500)

      return () => clearTimeout(timeoutId)
    }
  }, [
    selected,
    isHotspotProcessing,
    interactionType,
    minChoices,
    maxChoices,
    onSaveStudentAnswer
  ])

  return {
    selected,
    onSelect,
    responseDeclaration,
    isValid,
    disabled,
    isHotspotProcessing
  }
}
